import { addDomainSchema, MAX_DOMAINS_PER_LOCAL, type DomainDto, type DomainsListDto } from '@gl/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { ExternalLink, Globe, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { FormField } from '@/components/FormField';
import { Spinner } from '@/components/FullScreenLoader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ApiError, errorMessage } from '@/lib/api';
import { DnsInstructions } from './DnsInstructions';
import { DomainStatusBadge } from './DomainStatusBadge';
import { useAddDomain, useRemoveDomain, useVerifyDomain } from './useDomains';

interface ConnectDomainCardProps {
  localId: string;
  isDemo: boolean;
  data: DomainsListDto | undefined;
  isPending: boolean;
  error: unknown;
}

const FIELD_ERROR_CODES = new Set([
  'DOMAIN_INVALID',
  'DOMAIN_APEX_NOT_ALLOWED',
  'DOMAIN_WILDCARD_NOT_ALLOWED',
  'DOMAIN_RESERVED',
  'DOMAIN_TAKEN',
  'VALIDATION_ERROR',
]);

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
}

function verifyToast(d: DomainDto) {
  switch (d.status) {
    case 'ACTIVE':
      toast.success(`¡Listo! ${d.hostname} ya está activo`);
      break;
    case 'PENDING_SSL':
      toast.info('El DNS está bien. Estamos emitiendo el certificado, puede tardar unos minutos.');
      break;
    case 'PENDING_DNS':
      toast.info('Todavía no vemos el registro DNS. Puede tardar hasta 1 hora: probá de nuevo en un rato.');
      break;
    default:
      toast.error(d.lastError ?? 'Hubo un problema con el dominio');
  }
}

export function ConnectDomainCard({ localId, isDemo, data, isPending, error }: ConnectDomainCardProps) {
  const [instructionsFor, setInstructionsFor] = useState<string | null>(null);
  const [toRemove, setToRemove] = useState<DomainDto | null>(null);

  const form = useForm({
    resolver: zodResolver(addDomainSchema),
    defaultValues: { hostname: '' },
  });
  const add = useAddDomain(localId);
  const verify = useVerifyDomain(localId);
  const remove = useRemoveDomain(localId);

  const domains = data?.domains ?? [];
  const atLimit = domains.length >= MAX_DOMAINS_PER_LOCAL;
  const instructionsDomain = domains.find((d) => d.hostname === instructionsFor && d.status !== 'ACTIVE');

  const onSubmit = form.handleSubmit((v) =>
    add.mutate(v, {
      onSuccess: (d) => {
        toast.success(`Agregamos ${d.hostname}. Ahora creá el registro DNS.`);
        form.reset({ hostname: '' });
        setInstructionsFor(d.hostname);
      },
      onError: (err) => {
        if (err instanceof ApiError && FIELD_ERROR_CODES.has(err.code)) {
          form.setError('hostname', { message: err.message });
        } else {
          toast.error(errorMessage(err));
        }
      },
    }),
  );

  const confirmRemove = () => {
    if (!toRemove) return;
    const target = toRemove;
    remove.mutate(target, {
      onSuccess: () => {
        toast.success('Dominio quitado');
        if (instructionsFor === target.hostname) setInstructionsFor(null);
        setToRemove(null);
      },
      onError: (err) => toast.error(errorMessage(err)),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dominio propio</CardTitle>
        <CardDescription>
          Usá un subdominio tuyo para el menú, por ejemplo menu.tuheladeria.com.ar. Podés conectar hasta{' '}
          {MAX_DOMAINS_PER_LOCAL}.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-5">
        {isPending ? (
          <div className="flex justify-center py-4">
            <Spinner className="size-5 text-muted-foreground" />
          </div>
        ) : error ? (
          <p className="text-sm text-destructive">{errorMessage(error)}</p>
        ) : domains.length === 0 ? (
          <div className="flex items-center gap-3 rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            <Globe className="size-5 shrink-0" />
            Todavía no conectaste ningún dominio. Tu menú funciona igual con la dirección por defecto.
          </div>
        ) : (
          <ul className="grid gap-3">
            {domains.map((d) => {
              const checked = formatDate(d.lastCheckedAt);
              const verifying = verify.isPending && verify.variables?.id === d.id;
              return (
                <li key={d.id} className="grid gap-2 rounded-lg border p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <a
                        href={d.url}
                        target="_blank"
                        rel="noreferrer"
                        className="truncate font-mono text-sm font-medium hover:underline"
                      >
                        {d.hostname}
                      </a>
                      {d.status === 'ACTIVE' && <ExternalLink className="size-3.5 shrink-0 text-muted-foreground" />}
                      <DomainStatusBadge status={d.status} />
                    </div>
                    <div className="flex items-center gap-1">
                      {d.status !== 'ACTIVE' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setInstructionsFor(instructionsFor === d.hostname ? null : d.hostname)}
                        >
                          Instrucciones
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={verifying}
                        onClick={() =>
                          verify.mutate(d, { onSuccess: verifyToast, onError: (err) => toast.error(errorMessage(err)) })
                        }
                      >
                        {verifying ? <Spinner /> : <RefreshCw />} Verificar
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        aria-label={`Quitar ${d.hostname}`}
                        disabled={isDemo}
                        onClick={() => setToRemove(d)}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </div>
                  {d.lastError && d.status !== 'ACTIVE' && (
                    <p className="text-xs text-destructive">Cloudflare dice: {d.lastError}</p>
                  )}
                  {checked && <p className="text-xs text-muted-foreground">Último chequeo: {checked}</p>}
                </li>
              );
            })}
          </ul>
        )}

        {instructionsDomain && data && (
          <DnsInstructions hostname={instructionsDomain.hostname} cnameTarget={data.cnameTarget} />
        )}

        <form className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-start" onSubmit={onSubmit} noValidate>
          <FormField
            label="Conectar un dominio"
            htmlFor="domain-hostname"
            error={form.formState.errors.hostname?.message}
            hint={
              atLimit
                ? `Llegaste al máximo de ${MAX_DOMAINS_PER_LOCAL} dominios.`
                : 'Tiene que ser un subdominio (no el dominio pelado).'
            }
          >
            <Input
              id="domain-hostname"
              placeholder="menu.tuheladeria.com.ar"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="url"
              disabled={atLimit || isDemo}
              {...form.register('hostname')}
            />
          </FormField>
          <Button type="submit" className="sm:mt-[1.375rem]" disabled={add.isPending || atLimit || isDemo || !data}>
            {add.isPending ? <Spinner /> : <Plus />} Agregar
          </Button>
        </form>
        {isDemo && <p className="-mt-2 text-xs text-muted-foreground">En la demo no se pueden conectar dominios.</p>}
      </CardContent>

      <Dialog open={!!toRemove} onOpenChange={(open) => !open && setToRemove(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Quitar {toRemove?.hostname}?</DialogTitle>
            <DialogDescription>
              El menú deja de verse en ese dominio y los QR que lo usen dejan de funcionar. Después podés borrar el
              registro CNAME de tu DNS.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancelar</Button>
            </DialogClose>
            <Button variant="destructive" disabled={remove.isPending} onClick={confirmRemove}>
              {remove.isPending && <Spinner />} Quitar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
